import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import { Toaster } from 'react-hot-toast';

import { AppLayout } from './layouts/app-layout';
import { WSContextProvider } from './components/ws-context-provider';
import { GlobalStyle } from './themes/global-style';
import { useSession } from './auth/use-session';

import { QueueListPage } from './pages/queue-list-page';
import { QueueDetailPage } from './pages/queue-detail/queue-detail-page';
import { EnterTicketPinPage } from './pages/enter-ticket-pin-page';
import { RemoteCheckinPage } from './pages/queue-detail/remote-checkin-page';
import { SharingInfoPage } from './pages/queue-detail/sharing-info-page';
import { LeaveQueuePage } from './pages/queue-detail/leave-queue-page';
import { EnterProviderCodePage } from './pages/enter-provider-code-page';
import { UserProfilePage } from './pages/user-profile-page';
import { SignInPage } from './pages/auth/sign-in-page';
import { MagicLinkPage } from './pages/auth/magic-link-page';

/**
 * HashRouter, not BrowserRouter.
 *
 * The magic-link token lives in the URL fragment so it is never sent to a
 * server or written into an access log, and hash routing means the static
 * bundle needs no rewrite rule in Caddy to serve a deep link.
 */

/** Replaces SuperTokens' <SessionAuth>. */
function RequireSession({ children }: { children: React.ReactNode }) {
  const session = useSession();
  if (!session) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

/** Signed-in users have no business on the sign-in screen. */
function RedirectIfSignedIn({ children }: { children: React.ReactNode }) {
  const session = useSession();
  if (session) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Main() {
  return (
    <StrictMode>
      <RecoilRoot>
        <GlobalStyle />
        <WSContextProvider>
          <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route
                path="/auth"
                element={
                  <RedirectIfSignedIn>
                    <SignInPage />
                  </RedirectIfSignedIn>
                }
              />
              {/* Not wrapped in RedirectIfSignedIn: following a link while
                  already signed in should switch to that account, not bounce
                  to the home screen with the old one. */}
              <Route path="/auth/magic/:token" element={<MagicLinkPage />} />

              <Route
                path="/"
                element={
                  <RequireSession>
                    <AppLayout />
                  </RequireSession>
                }
              >
                <Route index element={<QueueListPage />} />
                <Route path="queues/:queueId" element={<QueueDetailPage />} />
                <Route path="queues/:queueId/enter-ticket-pin" element={<EnterTicketPinPage />} />
                <Route path="queues/:queueId/leave-queue" element={<LeaveQueuePage />} />
                <Route path="queues/:queueId/remote-checkin" element={<RemoteCheckinPage />} />
                <Route path="queues/:queueId/sharing-info" element={<SharingInfoPage />} />
                <Route path="enter-provider-code" element={<EnterProviderCodePage />} />
                <Route path="user-profile" element={<UserProfilePage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </HashRouter>
        </WSContextProvider>
      </RecoilRoot>
      <Toaster
        position="top-center"
        gutter={8}
        toastOptions={{
          duration: 5000,
          style: { background: '#363636', color: '#fff' },
          success: { duration: 3000 }
        }}
      />
    </StrictMode>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(<Main />);

/**
 * The service worker is what makes the app installable. Production only: in
 * development it would cache the shell and hide every change behind a reload.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err: unknown) => {
      console.warn('service worker registration failed', err);
    });
  });
}
